import {
  IAgencyLineItemImperial,
  IAgencyLineItemMetric,
  ILccaActivityImperial,
  ILccaActivityMetric,
  ILccaAlternativeImperial,
  ILccaAlternativeMetric,
  ILccaCalculatedImperial,
  ILccaCalculatedMetric,
} from '@/modules/lcca/interfaces/lccaCalculated';
import {
  ImperialUnits,
  KGM3_TO_LBFT3, LBFT3_TO_KGM3,
  M2CM_to_YD2IN,
  MetricUnits,
  roundAfterUnitConversion,
  UnitMapImperialToMetric,
  UnitMapMetricToImperial,
  YD2IN_TO_M2CM,
} from '@/lib/unitConversion/unitConversionFactors';
import { UnitSystem } from '@/interfaces/units';
import convert from 'convert-units';


export const lccaImperialToMetric = (A: ILccaCalculatedImperial): ILccaCalculatedMetric => {
  const B: ILccaCalculatedMetric = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    unitSystem: UnitSystem.Metric,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,
    state: A.state,

    name: A.name,

    // Scenario Information

    analysisDirection: A.analysisDirection, // mostly unused
    analyzedBy: A.analyzedBy,
    projectExtent_begin: A.projectExtent_begin, // Basically unused
    projectExtent_end: A.projectExtent_end, // ditto
    comments: A.comments,

    maxQueueLength: A.maxQueueLength,
    // probabilistic: A.probabilistic,

    // Analysis Options
    analysisPeriodLength_yrs: A.analysisPeriodLength_yrs, // analysisPeriod
    analysisPeriodStart_yr: A.analysisPeriodStart_yr, // beginningAnalysisPeriod -> sometimes used improperly in legacy
    userCostComputationMethod: A.userCostComputationMethod,
    userCostsInAnalysis: A.userCostsInAnalysis,

    discountRate: A.discountRate,

    // Traffic Data
    trafficYear: A.trafficYear,

    annualGrowthTraffic: A.annualGrowthTraffic,
    singleTruckAadtPct: A.singleTruckAadtPct, // singleTruckAADT
    combinationTruckAadtPct: A.combinationTruckAadtPct, // combinationTruckAADT
    numLanesNormal: A.numLanesNormal,


    aadtAnalysisPeriod: A.aadtAnalysisPeriod,
    freeFlowCapacity: A.freeFlowCapacity, // vphpl
    speedLimitNormal_kph: roundAfterUnitConversion(
      convert(A.speedLimitNormal_mph || 0).from(ImperialUnits.SPEED_MPH).to(MetricUnits.SPEED_KPH),
    ),
    maxQueueLength_km: roundAfterUnitConversion(
      convert(A.maxQueueLength_mi || 0).from(ImperialUnits.LENGTH_MI).to(MetricUnits.LENGTH_KM),
    ),
    noShowPct: A.noShowPct,
    detourPct: A.detourPct,
    additionalDetourTime_min: A.additionalDetourTime_min,


    // Traffic Hourly Distribution
    trafficHourlyDistribution: A.trafficHourlyDistribution,
    patternType: A.patternType,

    probabilistic: A.probabilistic,

    passengerCarsTime: 0,
    combinationTrucksTime: 0,
    singleUnitTrucksTime: 0,

    alternatives: A.alternatives.map((alt) => alternativeImperialToMetric(alt)),
  };

  return B;
};

export const lccaMetricToImperial = (A: ILccaCalculatedMetric): ILccaCalculatedImperial => {
  const B: ILccaCalculatedImperial = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    unitSystem: UnitSystem.Imperial,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,
    state: A.state,

    name: A.name,

    // Scenario Information

    analysisDirection: A.analysisDirection, // mostly unused
    analyzedBy: A.analyzedBy,
    projectExtent_begin: A.projectExtent_begin, // Basically unused
    projectExtent_end: A.projectExtent_end, // ditto
    comments: A.comments,

    maxQueueLength: A.maxQueueLength,
    // probabilistic: A.probabilistic,

    // Analysis Options
    analysisPeriodLength_yrs: A.analysisPeriodLength_yrs, // analysisPeriod
    analysisPeriodStart_yr: A.analysisPeriodStart_yr, // beginningAnalysisPeriod -> sometimes used improperly in legacy
    userCostComputationMethod: A.userCostComputationMethod,
    userCostsInAnalysis: A.userCostsInAnalysis,

    discountRate: A.discountRate,

    // Traffic Data
    trafficYear: A.trafficYear,

    annualGrowthTraffic: A.annualGrowthTraffic,
    singleTruckAadtPct: A.singleTruckAadtPct, // singleTruckAADT
    combinationTruckAadtPct: A.combinationTruckAadtPct, // combinationTruckAADT
    numLanesNormal: A.numLanesNormal,


    aadtAnalysisPeriod: A.aadtAnalysisPeriod,
    freeFlowCapacity: A.freeFlowCapacity, // vphpl
    speedLimitNormal_mph: roundAfterUnitConversion(
      convert(A.speedLimitNormal_kph || 0).from(MetricUnits.SPEED_KPH).to(ImperialUnits.SPEED_MPH),
    ),
    maxQueueLength_mi: roundAfterUnitConversion(
      convert(A.maxQueueLength_km || 0).from(MetricUnits.LENGTH_KM).to(ImperialUnits.LENGTH_MI),
    ),
    noShowPct: A.noShowPct,
    detourPct: A.detourPct,
    additionalDetourTime_min: A.additionalDetourTime_min,


    // Traffic Hourly Distribution
    trafficHourlyDistribution: A.trafficHourlyDistribution,
    patternType: A.patternType,

    probabilistic: A.probabilistic,

    passengerCarsTime: 0,
    combinationTrucksTime: 0,
    singleUnitTrucksTime: 0,

    alternatives: A.alternatives.map((alt) => alternativeMetricToImperial(alt)),
  };

  return B;
};

export const alternativeImperialToMetric = (A: ILccaAlternativeImperial): ILccaAlternativeMetric => {
  const B: ILccaAlternativeMetric = {
    id: A.id,
    name: A.name,
    index: A.index,
    unitSystem: UnitSystem.Metric,
    activities: A.activities.map((act) => activityImperialToMetric(act)),
  };
  return B;
};

export const alternativeMetricToImperial = (A: ILccaAlternativeMetric): ILccaAlternativeImperial => {
  const B: ILccaAlternativeImperial = {
    id: A.id,
    name: A.name,
    index: A.index,
    unitSystem: UnitSystem.Imperial,
    activities: A.activities.map((act) => activityMetricToImperial(act)),
  };
  return B;
};

export const activityImperialToMetric = (A: ILccaActivityImperial): ILccaActivityMetric => {
  const B: ILccaActivityMetric = {

    id: A.id,
    unitSystem: UnitSystem.Metric,
    name: A.name,
    description: A.description,
    index: A.index,
    alternative: A.alternative,
    startYear: A.startYear,
    serviceLife_yrs: A.serviceLife_yrs,
    structuralLife_yrs: A.structuralLife_yrs,
    agencyConstructionCost_thousands: A.agencyConstructionCost_thousands,
    agencyMaintenanceCost_thousands: A.agencyMaintenanceCost_thousands,

    userCostEnabled: A.userCostEnabled,
    workzoneLength_km: A.workzoneLength_mi
      ? roundAfterUnitConversion(convert(A.workzoneLength_mi).from(ImperialUnits.LENGTH_MI).to(MetricUnits.LENGTH_KM))
      : null,
    workzoneCapacity: A.workzoneCapacity,
    workzoneNumLanes: A.workzoneNumLanes,
    workzoneSpeedLimit_kph: A.workzoneSpeedLimit_mph
      ? roundAfterUnitConversion(convert(A.workzoneSpeedLimit_mph).from(ImperialUnits.SPEED_MPH).to(MetricUnits.SPEED_KPH))
      : null,
    workzoneDuration_days: A.workzoneDuration_days,

    userWorkzoneCost_thousands: A.userWorkzoneCost_thousands,

    laneClosePeriods: A.laneClosePeriods,
    nightShift: A.nightShift,

    lineItems: A.lineItems.map((item) => lineItemImperialToMetric(item)),
  };

  return B;
};

export const activityMetricToImperial = (A: ILccaActivityMetric): ILccaActivityImperial => {
  const B: ILccaActivityImperial = {

    id: A.id,
    unitSystem: UnitSystem.Imperial,
    name: A.name,
    description: A.description,
    index: A.index,
    alternative: A.alternative,
    startYear: A.startYear,
    serviceLife_yrs: A.serviceLife_yrs,
    structuralLife_yrs: A.structuralLife_yrs,
    agencyConstructionCost_thousands: A.agencyConstructionCost_thousands,
    agencyMaintenanceCost_thousands: A.agencyMaintenanceCost_thousands,

    userCostEnabled: A.userCostEnabled,
    workzoneLength_mi: A.workzoneLength_km
      ? roundAfterUnitConversion(convert(A.workzoneLength_km).from(MetricUnits.LENGTH_KM).to(ImperialUnits.LENGTH_MI))
      : null,
    workzoneCapacity: A.workzoneCapacity,
    workzoneNumLanes: A.workzoneNumLanes,
    workzoneSpeedLimit_mph: A.workzoneSpeedLimit_kph
      ? roundAfterUnitConversion(convert(A.workzoneSpeedLimit_kph).from(MetricUnits.SPEED_KPH).to(ImperialUnits.SPEED_MPH))
      : null,
    workzoneDuration_days: A.workzoneDuration_days,

    userWorkzoneCost_thousands: A.userWorkzoneCost_thousands,

    laneClosePeriods: A.laneClosePeriods,
    nightShift: A.nightShift,

    lineItems: A.lineItems.map((item) => lineItemMetricToImperial(item)),
  };

  return B;
};


export const convertImperialToMetric = function (unit: ImperialUnits | null, value: number, skipRounding: Boolean = false): number {
  let converted = null;

  if (unit === ImperialUnits.VOLUME_YD2IN) {
    converted = value * YD2IN_TO_M2CM;
  } else if (unit === ImperialUnits.DENSITY_LBFT3) {
    converted = value * LBFT3_TO_KGM3;
  } else if (unit !== ImperialUnits.UNITS) {
    // @ts-ignore
    converted = convert(value).from(unit).to(UnitMapImperialToMetric[unit]);
  } else {
    converted = value;
  }
  return skipRounding ? converted : roundAfterUnitConversion(converted);
};

export const convertMetricToImperial = function (unit: MetricUnits | null, value: number, skipRounding: Boolean = false): number {
  let converted = null;
  if (unit === MetricUnits.VOLUME_M2CM) {
    converted = value * M2CM_to_YD2IN;
  } else if (unit === MetricUnits.DENSITY_KGM3) {
    converted = value * KGM3_TO_LBFT3;
  } else if (unit !== MetricUnits.UNITS) {
    // @ts-ignore
    converted = convert(value).from(unit).to(UnitMapMetricToImperial[unit]);
  } else {
    converted = value;
  }
  return skipRounding ? converted : roundAfterUnitConversion(converted);
};

export const lineItemImperialToMetric = (A: IAgencyLineItemImperial): IAgencyLineItemMetric => {
  const B: IAgencyLineItemMetric = {
    id: A.id,
    unitSystem: UnitSystem.Metric,
    name: A.name,
    units: (function () {
      if (A.units !== null && Object.keys(UnitMapImperialToMetric).includes(A.units)) {
        return UnitMapImperialToMetric[A.units];
      }
      return MetricUnits.UNITS;
    }()),
    unitDescr: A.unitDescr,
    unitCost: A.unitCost / convertImperialToMetric(A.units, 1, true),
    quantity: convertImperialToMetric(A.units, A.quantity),
    // function(){
    // if (A.units !== null && A.units !== ImperialUnits.OTHER){
    //   @ts-ignore
    // return roundAfterUnitConversion(convert(A.quantity).from(A.units).to(UnitMapImperialToMetric[A.units]))
    // }
    // return A.quantity
    // }()
  };

  return B;
};


export const lineItemMetricToImperial = (A: IAgencyLineItemMetric): IAgencyLineItemImperial => {
  const B: IAgencyLineItemImperial = {
    id: A.id,
    unitSystem: UnitSystem.Imperial,
    name: A.name,
    units: (function () {
      // console.log(A.units, UnitMapImperialToMetric, UnitMapMetricToImperial)
      // console.log(Object.keys(UnitMapMetricToImperial).includes(A.units), UnitMapMetricToImperial[A.units]);
      if (A.units !== null && Object.keys(UnitMapMetricToImperial).includes(A.units)) {
        return UnitMapMetricToImperial[A.units];
      }
      return ImperialUnits.UNITS;
    }()),
    unitDescr: A.unitDescr,
    unitCost: A.unitCost / convertMetricToImperial(A.units, 1, true),
    quantity: convertMetricToImperial(A.units, A.quantity),
    // quantity: function(){
    //   console.log('conversion', A.units, UnitMapMetricToImperial[A.units])
    //   if (A.units !== null && A.units !== MetricUnits.OTHER){
    //     //@ts-ignore
    //     return roundAfterUnitConversion(convert(A.quantity).from(A.units).to(UnitMapMetricToImperial[A.units]))
    //   }
    //   return A.quantity
    // }()
  };

  return B;
};
